'use client'

import {
  Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  type RowSelectionState,
  type ColumnPinningState,
} from '@tanstack/react-table'

// 扩展 ColumnDef 类型以支持 pin 属性
type ExtendedColumnDef<TData, TValue> = ColumnDef<TData, TValue> & {
  pin?: 'left' | 'right'
}

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslations } from 'next-intl'
import { type CSSProperties, useState, useMemo } from 'react'
import { cn } from '@/lib/utils'

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  className?: string
  tableHeadClassName?: string
  tableCellClassName?: string
  tableHeaderClassName?: string
  onRowClick?: (event: any, row: any) => void
  onRowSelectionChange?: (selected: string[]) => void
}

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  tableHeadClassName = '',
  tableCellClassName = '',
  tableHeaderClassName = '',
  onRowClick,
  onRowSelectionChange,
}: DataTableProps<TData, TValue>) {
  const t = useTranslations('database')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // 从列配置中提取固定信息
  const columnPinning = useMemo((): ColumnPinningState => {
    const left: string[] = []
    const right: string[] = []

    columns.forEach((column) => {
      const extendedColumn = column as ExtendedColumnDef<TData, TValue>
      if (extendedColumn.pin === 'left' && extendedColumn.id) {
        left.push(extendedColumn.id)
      } else if (extendedColumn.pin === 'right' && extendedColumn.id) {
        right.push(extendedColumn.id)
      }
    })

    return { left, right }
  }, [columns])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater
      setRowSelection(newSelection)

      if (onRowSelectionChange) {
        const selectedIds = Object.keys(newSelection)
        onRowSelectionChange(selectedIds)
      }
    },
    state: {
      rowSelection,
      columnPinning,
    },
  })

  const handleRowClick = (event: any, row: any) => {
    onRowClick?.(event, row)
  }

  const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
    const isPinned = column.getIsPinned()
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left')
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right')

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-4px 0 4px -4px gray inset'
        : isFirstRightPinnedColumn
          ? '4px 0 4px -4px gray inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
    }
  }

  return (
    <Table className={className} style={{ flex: 1 }}>
      <TableHeader
        className={cn('sticky top-0 z-10 bg-muted', tableHeaderClassName)}
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const { column } = header
              const pinningStyles = getCommonPinningStyles(column)
              const fullStyle = {
                ...pinningStyles,
                width: `${header.getSize()}px`,
              }

              return (
                <TableHead
                  key={header.id}
                  className={tableHeadClassName}
                  style={fullStyle}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              )
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && 'selected'}
              onClick={(e) => handleRowClick(event, row)}
            >
              {row.getVisibleCells().map((cell) => {
                const pinningStyles = getCommonPinningStyles(cell.column)
                return (
                  <TableCell
                    key={cell.id}
                    className={tableCellClassName}
                    style={pinningStyles}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                )
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className="h-24 text-center text-gray-500"
            >
              {t('empty_table')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
